import React, { useState, useRef, useEffect, useMemo } from 'react';
import { TileLayer } from '@deck.gl/geo-layers';
import { BitmapLayer, ScatterplotLayer } from '@deck.gl/layers';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { Map, useMap } from '@vis.gl/react-google-maps';
import type { DeckProps } from '@deck.gl/core';
import type { ILocation } from '@map-bldr/shared';
import { Checkbox } from './ui/checkbox';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Available Street View coverage types that can be displayed on the map
 */
type StreetViewCoverageType = 'official' | 'unofficial' | 'combined' | 'none';

/**
 * Street View coverage display options
 */
interface StreetViewCoverageOptions {
  /** Show official Street View coverage (Google-operated vehicles) */
  official: boolean;
  /** Show unofficial Street View coverage (user-contributed 360° photos) */
  unofficial: boolean;
}

/**
 * Rendering options for the map overlay layers
 */
interface MapOverlayRenderOptions {
  /** Street View coverage layer settings */
  streetViewCoverage: StreetViewCoverageOptions;
  /** Opacity for Street View coverage overlay (0-1) */
  streetViewCoverageOpacity: number;
  // Future rendering options can be added here:
  // trafficLayer?: TrafficLayerOptions;
  // terrainOverlay?: TerrainOverlayOptions;
  // customDataLayers?: CustomDataLayerOptions[];
}

interface GoogleStreetViewCoverageMapProps {
  zoom?: number;
  streetViewCoverageOpacity?: number;
  locations?: ILocation[];
  selectedLocationId?: string | null;
  onAddLocation?: (lat: number, lng: number, panoId?: string, panoDate?: string) => void;
  onSelectLocation?: (locationId: string) => void;
}

interface CoverageControlsProps {
  coverageOptions: StreetViewCoverageOptions;
  onCoverageOptionsChange: (options: StreetViewCoverageOptions) => void;
}

interface DeckGLOverlayProps {
  renderOptions: MapOverlayRenderOptions;
  locations: ILocation[];
  selectedLocationId?: string | null;
  onAddLocation?: (lat: number, lng: number, panoId?: string, panoDate?: string) => void;
  onSelectLocation?: (locationId: string) => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Google Maps tile URLs for Street View coverage overlays
 */
const STREET_VIEW_COVERAGE_TILE_URLS: Record<Exclude<StreetViewCoverageType, 'none'>, string> = {
  official: 'https://www.google.com/maps/vt?pb=!1m7!8m6!1m3!1i{z}!2i{x}!3i{y}!2i9!3x1!2m8!1e2!2ssvv!4m2!1scc!2s*211m3*211e2*212b1*213e2*212b1*214b1!4m2!1ssvl!2s*211b0*212b1!3m8!2sen!3sus!5e1105!12m4!1e68!2m2!1sset!2sRoadmap!4e0!5m4!1e0!8m2!1e1!1e1!6m6!1e12!2i2!11e0!39b0!44e0!50e0',
  unofficial: 'https://www.google.com/maps/vt?pb=!1m7!8m6!1m3!1i{z}!2i{x}!3i{y}!2i9!3x1!2m8!1e2!2ssvv!4m2!1scc!2s*211m3*211e3*212b1*213e2*212b1*214b1!4m2!1ssvl!2s*211b0*212b1!3m8!2sen!3sus!5e1105!12m4!1e68!2m2!1sset!2sRoadmap!4e0!5m4!1e0!8m2!1e1!1e1!6m6!1e12!2i2!11e0!39b0!44e0!50e0',
  combined: 'https://www.google.com/maps/vt?pb=!1m7!8m6!1m3!1i{z}!2i{x}!3i{y}!2i9!3x1!2m8!1e2!2ssvv!4m2!1scc!2s*211m3*211e2*212b1*213e2*211m3*211e3*212b1*213e2!4m2!1ssvl!2s*211b0*212b1!3m8!2sen!3sus!5e1105!12m4!1e68!2m2!1sset!2sRoadmap!4e0!5m4!1e0!8m2!1e1!1e1!6m6!1e12!2i2!11e0!39b0!44e0!50e0',
};

/**
 * Carto basemap labels - designed for overlay with clean text
 * Only shows place names and POIs, no road drawings
 */
const LABELS_TILE_URL = 'https://a.basemaps.cartocdn.com/rastertiles/light_only_labels/{z}/{x}/{y}.png';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Determines which Street View coverage type to display based on user selection
 */
function getStreetViewCoverageType(options: StreetViewCoverageOptions): StreetViewCoverageType {
  if (options.official && options.unofficial) {
    return 'combined';
  } else if (options.official) {
    return 'official';
  } else if (options.unofficial) {
    return 'unofficial';
  } else {
    return 'none';
  }
}

// ============================================================================
// CONTROL LAYER COMPONENT
// ============================================================================

const ControlLayer: React.FC<CoverageControlsProps> = ({ coverageOptions, onCoverageOptionsChange }) => {
  return (
    <div className="absolute top-4 right-4 z-10 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg p-4 shadow-lg min-w-[180px]">
      <div className="mb-3 font-semibold text-sm font-mono text-gray-900">
        Street View Coverage
      </div>
      <div className="flex flex-col gap-3">
        <label className="flex items-center cursor-pointer text-sm font-mono text-gray-900">
          <Checkbox
            checked={coverageOptions.official}
            onCheckedChange={() => onCoverageOptionsChange({ ...coverageOptions, official: !coverageOptions.official })}
            className="mr-2"
          />
          Official
        </label>
        <label className="flex items-center cursor-pointer text-sm font-mono text-gray-900">
          <Checkbox
            checked={coverageOptions.unofficial}
            onCheckedChange={() => onCoverageOptionsChange({ ...coverageOptions, unofficial: !coverageOptions.unofficial })}
            className="mr-2"
          />
          Unofficial
        </label>
      </div>
    </div>
  );
};

// ============================================================================
// DECK.GL OVERLAY COMPONENT
// ============================================================================

const DeckGLOverlay: React.FC<DeckGLOverlayProps> = ({
  renderOptions,
  locations,
  selectedLocationId,
  onAddLocation,
  onSelectLocation
}) => {
  const map = useMap();
  const [isReady, setIsReady] = useState(false);
  const streetViewServiceRef = useRef<google.maps.StreetViewService | null>(null);
  const markerClickedRef = useRef(false);

  const overlay = useMemo(() => new GoogleMapsOverlay({
    layers: [],
  }), []);

  // Initialize Street View Service
  useEffect(() => {
    if (!streetViewServiceRef.current) {
      streetViewServiceRef.current = new google.maps.StreetViewService();
    }
  }, []);

  // Handle map clicks
  useEffect(() => {
    if (!map || !onAddLocation) return;

    const clickListener = map.addListener('click', async (e: google.maps.MapMouseEvent) => {
      if (!e.latLng || !streetViewServiceRef.current) return;

      // Check if a marker was clicked - if so, skip adding a new location
      if (markerClickedRef.current) {
        markerClickedRef.current = false;
        return;
      }

      const lat = e.latLng.lat();
      const lng = e.latLng.lng();

      // Check if Street View is available at this location
      try {
        const response = await streetViewServiceRef.current.getPanorama({
          location: { lat, lng },
          radius: 50, // Search within 50 meters
          source: google.maps.StreetViewSource.DEFAULT,
        });

        if (response && response.data && response.data.location) {
          // Use the actual Street View location (might be slightly different from click)
          const svLat = response.data.location.latLng?.lat() ?? lat;
          const svLng = response.data.location.latLng?.lng() ?? lng;

          // Extract panoId and panoDate
          const panoId = response.data.location.pano ?? undefined;
          const panoDate = response.data.imageDate ?? undefined;

          onAddLocation(svLat, svLng, panoId, panoDate);
        }
      } catch (error) {
        console.log('No Street View available at this location');
      }
    });

    return () => {
      google.maps.event.removeListener(clickListener);
    };
  }, [map, onAddLocation]);

  useEffect(() => {
    if (!map) return;

    const checkProjection = () => {
      if (map.getProjection()) {
        setIsReady(true);
        return true;
      }
      return false;
    };

    // Check immediately
    if (!checkProjection()) {
      // If not ready, wait for idle event
      const listener = map.addListener('idle', () => {
        if (checkProjection()) {
          google.maps.event.removeListener(listener);
        }
      });

      return () => {
        google.maps.event.removeListener(listener);
      };
    }
  }, [map]);

  useEffect(() => {
    if (isReady) {
      overlay.setMap(map);
      return () => overlay.setMap(null);
    }
  }, [map, overlay, isReady]);

  // Create tile layers based on render options
  const layers = useMemo(() => {
    const tileLayers: any[] = [];

    // Determine which Street View coverage type to display
    const coverageType = getStreetViewCoverageType(renderOptions.streetViewCoverage);

    if (coverageType !== 'none') {
      const coverageUrl = STREET_VIEW_COVERAGE_TILE_URLS[coverageType];

      tileLayers.push(
        new TileLayer({
          id: 'street-view-coverage',
          data: coverageUrl,
          minZoom: 0,
          maxZoom: 20,
          tileSize: 256,
          opacity: renderOptions.streetViewCoverageOpacity,
          renderSubLayers: (props) => {
            const {
              bbox: { west, south, east, north },
            } = props.tile;

            return new BitmapLayer(props, {
              data: null,
              image: props.data,
              bounds: [west, south, east, north],
            });
          },
        })
      );
    }

    // Add labels layer on top (always visible)
    tileLayers.push(
      new TileLayer({
        id: 'poi-labels',
        data: LABELS_TILE_URL,
        minZoom: 0,
        maxZoom: 20,
        tileSize: 256,
        renderSubLayers: (props) => {
          const {
            bbox: { west, south, east, north },
          } = props.tile;

          return new BitmapLayer(props, {
            data: null,
            image: props.data,
            bounds: [west, south, east, north],
          });
        },
      })
    );

    // Add location markers layer - black with bright outline
    if (locations.length > 0) {
      // Handler for marker clicks that prevents map click from firing
      const handleMarkerClick = (info: any) => {
        if (info.object && onSelectLocation) {
          markerClickedRef.current = true;
          onSelectLocation(info.object.id);
        }
      };

      tileLayers.push(
        // Bright outer ring - this is the only pickable layer
        new ScatterplotLayer({
          id: 'location-markers-outer',
          data: locations,
          getPosition: (d: ILocation) => [d.lng, d.lat],
          getFillColor: (d: ILocation) =>
            d.id === selectedLocationId
              ? [0, 255, 0, 255]  // Bright green for selected
              : [255, 255, 0, 255], // Bright yellow for unselected
          getRadius: 12,
          radiusMinPixels: 12,
          radiusMaxPixels: 12,
          pickable: true,
          onClick: handleMarkerClick,
          updateTriggers: {
            getFillColor: [selectedLocationId],
          },
        }),
        // Black main circle - not pickable to avoid click interference
        new ScatterplotLayer({
          id: 'location-markers-main',
          data: locations,
          getPosition: (d: ILocation) => [d.lng, d.lat],
          getFillColor: [0, 0, 0, 255], // Solid black
          getRadius: 9,
          radiusMinPixels: 9,
          radiusMaxPixels: 9,
          pickable: false,
        }),
        // Bright center dot - not pickable to avoid click interference
        new ScatterplotLayer({
          id: 'location-markers-center',
          data: locations,
          getPosition: (d: ILocation) => [d.lng, d.lat],
          getFillColor: (d: ILocation) =>
            d.id === selectedLocationId
              ? [0, 255, 0, 255]  // Bright green for selected
              : [255, 255, 0, 255], // Bright yellow for unselected
          getRadius: 3,
          radiusMinPixels: 3,
          radiusMaxPixels: 3,
          pickable: false,
          updateTriggers: {
            getFillColor: [selectedLocationId],
          },
        })
      );
    }

    // Future layer types can be added here:
    // if (renderOptions.trafficLayer) { ... }
    // if (renderOptions.terrainOverlay) { ... }

    return tileLayers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderOptions, locations, selectedLocationId, onSelectLocation]);

  // Update overlay layers when they change
  useEffect(() => {
    if (isReady) {
      overlay.setProps({ layers });
    }
  }, [isReady, overlay, layers]);

  return null;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const GoogleStreetViewCoverageMap: React.FC<GoogleStreetViewCoverageMapProps> = ({
  zoom = 2,
  streetViewCoverageOpacity = 0.6,
  locations = [],
  selectedLocationId,
  onAddLocation,
  onSelectLocation,
}) => {
  const [streetViewCoverageOptions, setStreetViewCoverageOptions] = useState<StreetViewCoverageOptions>({
    official: true,
    unofficial: false,
  });

  // Build render options from component state
  const renderOptions: MapOverlayRenderOptions = {
    streetViewCoverage: streetViewCoverageOptions,
    streetViewCoverageOpacity,
  };

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', cursor: 'crosshair' }}>
      <Map
        defaultCenter={{lat: 41.88895, lng: -87.62420}}
        defaultZoom={11}
        styles={[
          {
            featureType: "all",
            elementType: "labels",
            stylers: [{ visibility: "off" }]
          }
        ]}
        streetViewControl={false}
        fullscreenControl={false}
      >
        <DeckGLOverlay
          renderOptions={renderOptions}
          locations={locations}
          selectedLocationId={selectedLocationId}
          onAddLocation={onAddLocation}
          onSelectLocation={onSelectLocation}
        />
      </Map>
      <ControlLayer
        coverageOptions={streetViewCoverageOptions}
        onCoverageOptionsChange={setStreetViewCoverageOptions}
      />
    </div>
  );
};

export default GoogleStreetViewCoverageMap;
